import { useCallback, useEffect, useState } from 'react';

/**
 * A minimal dependency-free hash router. The route is the bare path after `#/` matched
 * against the app's known tabs; anything unknown falls back. Parsing is a pure function so
 * it is unit-testable without a DOM (CONVENTIONS: deterministic logic is pure).
 */
export function parseHash<T extends string>(hash: string, tabs: readonly T[], fallback: T): T {
  const raw = (hash.replace(/^#\/?/, '').split('?')[0] ?? '').trim();
  return (tabs as readonly string[]).includes(raw) ? (raw as T) : fallback;
}

export function hashFor<T extends string>(tab: T): string {
  return `#/${tab}`;
}

/** React binding over `parseHash`; re-renders on `hashchange`. */
export function useHashRoute<T extends string>(tabs: readonly T[], fallback: T): [T, (tab: T) => void] {
  const [route, setRoute] = useState<T>(() => parseHash(window.location.hash, tabs, fallback));

  useEffect(() => {
    const onChange = (): void => setRoute(parseHash(window.location.hash, tabs, fallback));
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, [tabs, fallback]);

  const navigate = useCallback((tab: T) => {
    window.location.hash = hashFor(tab);
  }, []);

  return [route, navigate];
}
